import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

const steps = [
  {
    num: '01',
    title: 'Анализ конкурентов',
    description: 'AIGAM определяет ваших ключевых конкурентов и находит их цифровые следы — соцсети, Telegram-каналы, комментарии, форумы, отзывы. Система строит карту аудитории каждого конкурента.',
    icon: 'ri-search-eye-line',
  },
  {
    num: '02',
    title: 'Сбор аудитории',
    description: 'Парсим открытые данные о пользователях, которые взаимодействуют с конкурентами: комментируют, задают вопросы, оставляют заявки. Фильтруем по коммерческим намерениям.',
    icon: 'ri-archive-line',
  },
  {
    num: '03',
    title: 'Выявление горячего спроса',
    description: 'AI анализирует тональность, срочность и контекст обращений. Определяем пользователей с максимальной вероятностью покупки в ближайшие 24–72 часа.',
    icon: 'ri-fire-line',
  },
  {
    num: '04',
    title: 'Запуск AI-контакта',
    description: 'AI-рассыльщик автоматически инициирует диалог с каждым горячим контактом через ваш мессенджер. Клиент получает релевантное предложение раньше, чем конкурент успевает ответить.',
    icon: 'ri-rocket-2-line',
  },
];

const sources = [
  {
    name: 'Telegram-каналы',
    description: 'Отслеживаем комментарии и обращения в каналах конкурентов, находим пользователей с вопросами о продуктах.',
    icon: 'ri-telegram-line',
  },
  {
    name: 'Социальные сети',
    description: 'Мониторим VK, Instagram, Facebook — комментарии под постами конкурентов, обсуждения в группах.',
    icon: 'ri-share-line',
  },
  {
    name: 'Отзовики и форумы',
    description: 'Анализируем площадки с отзывами и форумы — пользователи, сравнивающие продукты и ищущие рекомендации.',
    icon: 'ri-chat-quote-line',
  },
  {
    name: 'Поисковые запросы',
    description: 'Отслеживаем публичные сигналы о намерении покупки: запросы сравнения, альтернативы, отзывы о конкурентах.',
    icon: 'ri-globe-line',
  },
];

export default function CompHow() {
  const { ref: labelRef, revealed: labelRevealed } = useScrollReveal();
  const { ref: titleRef, revealed: titleRevealed } = useScrollReveal({ delay: 150 });
  const { containerRef: stepsRef, revealedCount: stepsRevealed } = useScrollRevealGroup<HTMLDivElement>(4);
  const { ref: sourcesTitleRef, revealed: sourcesTitleRevealed } = useScrollReveal();
  const { containerRef: sourcesRef, revealedCount: sourcesRevealed } = useScrollRevealGroup<HTMLDivElement>(4);

  return (
    <section id="comp-how" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="mb-12 md:mb-16 max-w-[640px]">
          <span ref={labelRef} className={`text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${labelRevealed ? 'revealed' : ''}`}>
            Как работает
          </span>
          <h2 ref={titleRef} className={`mt-4 font-display font-extrabold text-[34px] md:text-[46px] leading-[1.1] tracking-tightest text-foreground-950 reveal-fade-up ${titleRevealed ? 'revealed' : ''}`}>
            Алгоритм перехвата:
            <br />
            от анализа до сделки
          </h2>
        </div>

        {/* Шаги */}
        <div ref={stepsRef} className="relative mb-16 md:mb-20">
          <div className="absolute left-[19px] md:left-[23px] top-4 bottom-4 w-px bg-background-200 hidden sm:block" />

          <div className="flex flex-col gap-10 md:gap-14">
            {steps.map((step, i) => (
              <div key={step.num} className={`flex gap-5 md:gap-8 reveal-fade-up ${i < stepsRevealed ? 'revealed' : ''}`} style={i < stepsRevealed ? { transitionDelay: `${i * 120}ms` } : undefined}>
                <div className="relative shrink-0">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary-500 flex items-center justify-center">
                    <span className="text-sm md:text-base font-bold text-background-50">
                      {step.num}
                    </span>
                  </div>
                </div>
                <div className="pt-1 md:pt-2 flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600">
                      <i className={`${step.icon} text-sm`}></i>
                    </span>
                    <h3 className="font-display font-bold text-[18px] text-foreground-950">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-[14.5px] text-foreground-500 leading-relaxed max-w-[560px]">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Источники */}
        <div>
          <h3 ref={sourcesTitleRef} className={`font-display font-extrabold text-[20px] md:text-[24px] text-foreground-950 mb-2 reveal-fade-up ${sourcesTitleRevealed ? 'revealed' : ''}`}>
            Источники данных
          </h3>
          <p className="text-[14.5px] text-foreground-500 mb-8 max-w-[560px]">
            AIGAM собирает данные из открытых источников, где пользователи публично проявляют интерес к продуктам конкурентов.
          </p>

          <div ref={sourcesRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {sources.map((src, i) => (
              <div
                key={src.name}
                className={`rounded-2xl bg-background-100 border border-background-200 p-5 md:p-6 hover:border-primary-500/40 transition-all duration-300 reveal-fade-up ${i < sourcesRevealed ? 'revealed' : ''}`}
                style={i < sourcesRevealed ? { transitionDelay: `${i * 100}ms` } : undefined}
              >
                <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 mb-3">
                  <i className={`${src.icon} text-base`}></i>
                </div>
                <h4 className="font-heading text-sm font-semibold text-foreground-950 mb-1.5">
                  {src.name}
                </h4>
                <p className="text-[13px] text-foreground-600 leading-relaxed">{src.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}